import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Loader2, Trophy, MapPin, CalendarDays, Youtube, ChevronLeft, ChevronRight, ScrollText, Swords, ArrowLeft } from "lucide-react";
import api, { fileUrl } from "@/lib/api";
import { Layout } from "@/components/Layout";
import { Seo, SITE_URL } from "@/components/Seo";
import { ZoomableImage } from "@/components/ZoomableImage";

const C = {
  bg: "#060807", surface: "#0E1310", elevated: "#151C18",
  gold: "#D4AF37", goldLight: "#F2E3A0", green: "#1D5C34", greenDark: "#123B21",
  silver: "#8A8D91", border: "#2A302C", text: "#F3F4F6", sub: "#9CA3AF",
};
const SERIF = "'Cormorant Garamond', serif";
const ytId = (url) => { const m = String(url || "").match(/(?:youtu\.be\/|v=|embed\/|shorts\/)([\w-]{11})/); return m ? m[1] : null; };
const ordinal = (n) => { const s = ["th", "st", "nd", "rd"], v = n % 100; return n + (s[(v - 20) % 10] || s[v] || s[0]); };

function Roster({ title, players, highlight }) {
  if (!players || players.length === 0) return null;
  return (
    <div className="rounded-sm border p-4" style={{ background: C.bg, borderColor: highlight ? C.gold : C.border }}>
      <p className="text-xs font-bold uppercase tracking-wider mb-2" style={{ color: highlight ? C.gold : C.silver }}>{title}</p>
      <ul className="space-y-1">
        {players.map((p, i) => (
          <li key={i} className="text-sm flex justify-between gap-2" style={{ color: C.text }}>
            <span>{typeof p === "string" ? p : p.name}</span>
            {typeof p === "object" && p.fargo && <span style={{ color: C.silver }}>{p.fargo}</span>}
          </li>
        ))}
      </ul>
    </div>
  );
}

function Badge({ status }) {
  const map = {
    live: { t: "Live", bg: "#E11D2A", fg: "#fff" },
    upcoming: { t: "Upcoming", bg: C.green, fg: "#fff" },
    completed: { t: "Completed", bg: C.gold, fg: C.bg },
  };
  const s = map[status] || map.completed;
  return <span data-testid={`bc-edition-status-${status}`} className="inline-flex items-center gap-1.5 rounded-sm px-3 py-1 text-xs font-bold uppercase tracking-wide" style={{ background: s.bg, color: s.fg }}>{status === "live" && <span className="relative flex h-2 w-2"><span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-white opacity-75" /><span className="relative inline-flex rounded-full h-2 w-2 bg-white" /></span>}{s.t}</span>;
}

export default function BraggersEdition() {
  const { slug } = useParams();
  const [d, setD] = useState(undefined);

  useEffect(() => {
    api.get(`/braggers/edition/${slug}`).then((r) => setD(r.data)).catch(() => setD(null));
  }, [slug]);

  if (d === undefined)
    return <Layout><div className="min-h-[60vh] flex items-center justify-center" style={{ background: C.bg }}><Loader2 className="h-8 w-8 animate-spin" style={{ color: C.gold }} /></div></Layout>;

  if (d === null)
    return (
      <Layout>
        <Seo title="Bragger's Cup Edition Not Found | Quinte BCA" noindex path={`/braggers/${slug}`} />
        <div className="min-h-[60vh] flex flex-col items-center justify-center text-center px-6" style={{ background: C.bg, color: C.text }} data-testid="bc-edition-404">
          <Trophy className="h-12 w-12 mb-4" style={{ color: C.gold }} />
          <h1 className="text-3xl font-medium" style={{ fontFamily: SERIF, color: C.goldLight }}>Edition not found</h1>
          <p className="mt-2 mb-6" style={{ color: C.sub }}>We couldn't find that Bragger's Cup match.</p>
          <Link to="/braggers" className="inline-flex items-center gap-2 px-6 py-3 rounded-sm font-bold" style={{ background: C.gold, color: C.bg }}><ArrowLeft className="h-4 w-4" /> All Bragger's Cup matches</Link>
        </div>
      </Layout>
    );

  const ch = d.edition;
  const { status } = d;
  const path = `/braggers/${ch.slug || slug}`;
  const winner = ch.winning_team || "Champions";
  const challenger = ch.challenger_team || "Challengers";
  const vs = ch.winning_team || ch.challenger_team ? `${winner} vs ${challenger}` : (ch.title || `Bragger's Cup ${ch.edition}`);
  const where = [ch.venue, ch.city, ch.province].filter(Boolean).join(", ");
  const vid = ytId(ch.youtube_url);
  const social = ch.team_photo || ch.scoresheet || d.logo;

  const title = ch.seo_title || (
    status === "completed" ? `${vs} – Bragger's Cup ${ch.edition} Results | Quinte BCA`
    : `${vs} – Bragger's Cup ${ch.edition} | Quinte BCA`
  );
  const description = ch.seo_description || (
    status === "completed"
      ? `View results, team rosters, Fargo ratings${ch.scoresheet ? ", scoresheet" : ""}${ch.youtube_url ? " and match replay" : ""} from the ${ordinal(ch.edition)} Quinte BCA Bragger's Cup Team Challenge between ${winner} and ${challenger}${ch.date ? ` (${ch.date})` : ""}.`
      : `Follow the ${ordinal(ch.edition)} Quinte BCA Bragger's Cup Team Challenge between ${winner} and ${challenger}${where ? ` at ${where}` : ""}${ch.date ? `, ${ch.date}` : ""}.`
  ).slice(0, 300);

  const sportsEvent = {
    "@context": "https://schema.org", "@type": "SportsEvent",
    name: `${vs} – Bragger's Cup ${ch.edition}`,
    description,
    eventStatus: status === "upcoming" ? "https://schema.org/EventScheduled" : "https://schema.org/EventScheduled",
    organizer: { "@type": "Organization", name: "Quinte BCA Pool League", url: SITE_URL },
    url: SITE_URL + path,
    ...(fileUrl(social) ? { image: fileUrl(social) } : {}),
    ...(where ? { location: { "@type": "Place", name: ch.venue || where, address: [ch.city, ch.province].filter(Boolean).join(", ") } } : {}),
    ...((ch.winning_team || ch.challenger_team) ? { competitor: [ch.winning_team, ch.challenger_team].filter(Boolean).map((t) => ({ "@type": "SportsTeam", name: t })) } : {}),
  };
  const crumbLd = {
    "@context": "https://schema.org", "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Home", item: `${SITE_URL}/` },
      { "@type": "ListItem", position: 2, name: "Bragger's Cup", item: `${SITE_URL}/braggers` },
      { "@type": "ListItem", position: 3, name: `${ordinal(ch.edition)} Edition`, item: SITE_URL + path },
    ],
  };

  return (
    <Layout>
      <Seo title={title} description={description} path={path} image={fileUrl(social)} type="article" noindex={ch.published === false} jsonLd={[sportsEvent, crumbLd]} />
      <div style={{ background: C.bg, color: C.text }} data-testid="braggers-edition-page">
        {/* HERO */}
        <section className="relative" style={{ background: `radial-gradient(circle at 70% 20%, ${C.greenDark}66, ${C.bg} 60%)` }}>
          <div className="max-w-5xl mx-auto px-6 lg:px-12 py-14 sm:py-20">
            <nav aria-label="Breadcrumb" className="text-sm mb-5" style={{ color: C.sub }} data-testid="bc-edition-breadcrumbs">
              <Link to="/" className="hover:underline">Home</Link> <span> / </span>
              <Link to="/braggers" className="hover:underline">Bragger's Cup</Link> <span> / </span>
              <span style={{ color: C.goldLight }}>{ordinal(ch.edition)} Edition</span>
            </nav>
            <div className="flex items-center gap-3 mb-4">
              <Badge status={status} />
              <span className="text-xs tracking-[0.25em] uppercase font-bold" style={{ color: C.gold }}>{ordinal(ch.edition)} Bragger's Cup Team Challenge</span>
            </div>
            <h1 className="text-4xl sm:text-6xl font-semibold tracking-tight leading-none" style={{ fontFamily: SERIF, color: C.goldLight }} data-testid="bc-edition-title">{vs}</h1>
            <div className="flex flex-wrap gap-x-6 gap-y-1 mt-5 text-sm" style={{ color: C.sub }}>
              {ch.date && <span className="inline-flex items-center gap-1.5"><CalendarDays className="h-4 w-4" style={{ color: C.gold }} /> {ch.date}</span>}
              {where && <span className="inline-flex items-center gap-1.5"><MapPin className="h-4 w-4" style={{ color: C.gold }} /> {where}</span>}
              {status === "completed" && ch.winning_team && <span className="inline-flex items-center gap-1.5" style={{ color: C.goldLight }}><Trophy className="h-4 w-4" style={{ color: C.gold }} /> Winner: {ch.winning_team}</span>}
            </div>
          </div>
        </section>

        <div className="max-w-5xl mx-auto px-6 lg:px-12 py-12 grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-7 space-y-8">
            {ch.team_photo && (
              <div className="rounded-sm overflow-hidden border" style={{ borderColor: C.border }}>
                <ZoomableImage src={fileUrl(ch.team_photo)} alt={`${winner} Bragger's Cup ${ch.edition} team`} testId="bc-edition-photo" className="w-full h-auto object-contain max-h-[70vh]" />
              </div>
            )}
            {ch.summary && <p className="leading-relaxed whitespace-pre-line" style={{ color: C.sub }}>{ch.summary}</p>}
            {ch.scoresheet && (
              <div>
                <p className="text-xs tracking-[0.2em] uppercase font-bold mb-2 flex items-center gap-1.5" style={{ color: C.silver }}><ScrollText className="h-4 w-4" style={{ color: C.gold }} /> Scoresheet</p>
                <div className="rounded-sm overflow-hidden border" style={{ borderColor: C.border }}>
                  <ZoomableImage src={fileUrl(ch.scoresheet)} alt={`Bragger's Cup ${ch.edition} ${winner} vs ${challenger} scoresheet`} testId="bc-edition-scoresheet" className="w-full h-auto object-contain max-h-[80vh]" />
                </div>
              </div>
            )}
          </div>

          <aside className="lg:col-span-5 space-y-6">
            {vid && (
              <div>
                <p className="text-xs tracking-[0.2em] uppercase font-bold mb-2 flex items-center gap-1.5" style={{ color: C.silver }}><Youtube className="h-4 w-4" style={{ color: C.gold }} /> Watch the Match</p>
                <div className="aspect-video rounded-sm overflow-hidden border" style={{ borderColor: C.border }}>
                  <iframe className="w-full h-full" src={`https://www.youtube.com/embed/${vid}`} title={`${vs} — Bragger's Cup ${ch.edition} replay`} loading="lazy" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen />
                </div>
                {ch.youtube_url && <a href={ch.youtube_url} target="_blank" rel="noopener noreferrer" data-testid="bc-edition-yt" className="inline-flex items-center gap-2 text-sm font-semibold mt-3" style={{ color: C.gold }}><Youtube className="h-4 w-4" /> Watch on YouTube</a>}
              </div>
            )}
            {(ch.winning_players?.length || ch.challenger_players?.length) ? (
              <div className="space-y-4">
                <p className="text-xs tracking-[0.2em] uppercase font-bold flex items-center gap-1.5" style={{ color: C.silver }}><Swords className="h-4 w-4" style={{ color: C.gold }} /> Rosters <span className="normal-case font-normal" style={{ color: C.sub }}>· FargoRate at time of match</span></p>
                <Roster title={winner} players={ch.winning_players} highlight />
                <Roster title={challenger} players={ch.challenger_players} />
              </div>
            ) : null}
          </aside>
        </div>

        {/* Prev / Next */}
        <div className="max-w-5xl mx-auto px-6 lg:px-12 pb-16">
          <div className="flex items-center justify-between border-t pt-6" style={{ borderColor: C.border }}>
            {d.prev ? <Link to={`/braggers/${d.prev}`} data-testid="bc-edition-prev" className="inline-flex items-center gap-1.5 text-sm font-semibold" style={{ color: C.gold }}><ChevronLeft className="h-4 w-4" /> Previous Bragger's Cup</Link> : <span />}
            <Link to="/braggers" className="text-sm font-semibold hover:underline" style={{ color: C.sub }}>View All Bragger's Cup</Link>
            {d.next ? <Link to={`/braggers/${d.next}`} data-testid="bc-edition-next" className="inline-flex items-center gap-1.5 text-sm font-semibold" style={{ color: C.gold }}>Next Bragger's Cup <ChevronRight className="h-4 w-4" /></Link> : <span />}
          </div>
        </div>
      </div>
    </Layout>
  );
}
